<template>
  <section class="inbox-conversations">
    <embed-frame
      class="inbox-conversations-frame"
      endpoint="inbox/conversations"
      placeholder="Carregando..."
      :settings="embedSettings"
      :callbacks="embedCallbacks"
    />
  </section>
</template>

<script>
  import EmbedFrame from '@sdk/views/Embed/Frame.vue';

  export default {
    name: 'InboxConversations',
    components: {
      EmbedFrame,
    },
    data() {
      // Snapshot the deep-link filters once so the embed seeds from them on
      // mount. Kept static (not reactive to the query) so the filter mirroring
      // below — which updates the URL — doesn't re-mount the embed in a loop.
      //
      // conversation_id rides the same rail as the filters, and it is what a
      // notification template's button links to: handed over, the embed opens
      // that thread on top of the seeded filters.
      return {
        embedSettings: {
          inbox: {
            q: this.$route.query.q || '',
            store_id: this.$route.query.store_id || '',
            conversation_id: this.$route.query.conversation_id || '',
            // Which read the inbox opens on: '' the thread pane, 'schedules' the
            // Agendamentos timeline. O VALOR É `schedules`, que é o nome que o próprio
            // inbox usa para essa leitura (contentView, useSchedules) e o que o funil
            // posta - escrever 'agenda' aqui seria a palavra da tela no lugar da chave.
            // Está no endereço porque essa timeline é a única agenda do produto, e o
            // quadro do funil linka direto para ela.
            view: this.$route.query.view || '',
            _enable_fullscreen: true,
          },
        },
      };
    },
    computed: {
      embedCallbacks() {
        return {
          'public_simulator_distribution:action': this.action,
          'public_simulator_conversation:filter': this.filter,
          'public_simulator_setting:action': this.openSettings,
          'simulation:read:action': this.openSimulation,
        };
      },
    },
    methods: {
      // Open a host route the embed asked for: a new tab when the embed flagged a
      // modifier-click (_blank), otherwise an in-place router navigation. The
      // router has no programmatic new-tab, so a new tab resolves the route to an
      // href and hands it to window.open.
      navigate(route, newTab) {
        if (newTab) {
          window.open(this.$router.resolve(route).href, '_blank');
        } else {
          this.$router.push(route);
        }
      },

      // Fired by the embed's "Distribuição de leads" button: open the selected
      // store's distribution form, or the showroom store listing when none is
      // selected (distribution now lives under each store's showroom).
      action(data) {
        if (data.action !== 'distribution/click') {
          return;
        }

        const route = data.settings.store_id
          ? { name: 'stores-public-simulator-distribution', params: { id: data.settings.store_id } }
          : { name: 'stores-public-simulator-list' };

        // eslint-disable-next-line no-underscore-dangle
        this.navigate(route, data.settings._blank);
      },

      // Fired by the thread header's "Reconectar" action when the line is down:
      // open the store's WhatsApp settings (the QR reconnect lives there). Always
      // a new tab so the seller keeps the open inbox thread.
      openSettings(data) {
        if (data.action !== 'public_simulator/settings/form') {
          return;
        }

        const route = {
          name: 'stores-public-simulator-settings',
          params: { id: data.settings.store_id },
        };

        // eslint-disable-next-line no-underscore-dangle
        this.navigate(route, data.settings._blank);
      },

      // Fired by the embed's "Abrir simulação" button on a results bubble: open
      // the simulation result page for that lead's pick.
      openSimulation(data) {
        if (data.action !== 'simulations/read') {
          return;
        }

        const route = {
          name: 'simulations-read',
          params: { uuid: data.settings.simulation_uuid },
          query: data.settings.store_id ? { store_id: data.settings.store_id } : {},
        };

        // eslint-disable-next-line no-underscore-dangle
        this.navigate(route, data.settings._blank);
      },

      // Fired as the embed's search/store filters change: mirror them into the
      // URL so the page is shareable and survives a reload. A client-side
      // replace (not push) keeps it out of history and off the embed's settings
      // snapshot, so it doesn't re-mount the embed.
      filter(data) {
        const query = {};

        if (data.q) {
          query.q = data.q;
        }

        if (data.store_id) {
          query.store_id = data.store_id;
        }

        // Which thread is open goes to the URL too, so the address the seller
        // shares (or reloads) is the same one a notification's button hands
        // over. None open comes as null, and drops out of the URL.
        if (data.conversation_id) {
          query.conversation_id = data.conversation_id;
        }

        // And which read is open, for the same reason: a shared link that landed
        // on a thread when the agenda was showing would not be the page shared.
        if (data.view) {
          query.view = data.view;
        }

        this.$router.replace({ query }).catch(() => {});
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .inbox-conversations {
    padding: 0 var(--product--pwa--horizontal);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: 0 var(--product--pwa--horizontal);
    }
  }
</style>
